import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Building2, IndianRupee, MapPin, MessageSquare, Phone, Plus, ShieldCheck, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime, money, telLink, waLink, pingNotifications } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { useConfig } from "@/lib/useConfig";
import { PageHead } from "@/components/Bits";
import { ProformaDialog } from "@/components/ProformaDialog";
import { ExecutivesManager } from "@/components/ExecutivesManager";
import { OutcomeSelect } from "@/components/OutcomeSelect";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DEFAULT_STAGES = [
  "Lead", "Interested", "Follow-up", "Visit booked", "Visited", "Proforma", "Payment",
  "Dispatch", "Delivery", "First work", "Next purchases", "Accredited detailer",
  "Monthly purchases",
];

const STAGE_HINT = {
  Lead: "A detailer we have just heard of or reached out to.",
  Interested: "They replied and want to know more.",
  "Follow-up": "Waiting on them — keep the conversation warm.",
  "Visit booked": "A date is set to see their shop.",
  Visited: "We have seen the shop and sized up the opportunity.",
  Proforma: "Proforma invoice sent with the material list.",
  Payment: "Payment agreed or partly received.",
  Dispatch: "Material packed and on its way.",
  Delivery: "Material reached the detailer.",
  "First work": "They installed their first car with our film.",
  "Next purchases": "They came back for a repeat order.",
  "Accredited detailer": "Signed up as a Veloskin detailer — give them a login.",
  "Monthly purchases": "Buying every month. Real order value shows on the card.",
};

const KINDS = ["Call", "WhatsApp", "Visit", "Email", "Meeting", "Note"];
const KIND_DOT = {
  Call: "bg-brand",
  WhatsApp: "bg-emerald-500",
  Visit: "bg-amber-500",
  Meeting: "bg-indigo-500",
  Email: "bg-sky-500",
  Note: "bg-slate-400",
};
const blankForm = {
  business_name: "", owner_name: "", mobile: "", whatsapp: "", email: "", city: "", state: "", region: "",
  gstin: "", address: "",
  stage: "Lead", expected_value: "", proforma_value: "", payment_received: "",
  next_followup: "", next_followup_note: "", notes: "",
};

const EXEC_STAGES = ["Lead", "Interested", "Follow-up", "Visit booked", "First purchase"];

export default function B2BPipeline() {
  const { user } = useAuth();
  const canWrite = can(user, "b2b:write");
  const isExec = user.role === "central_sales_executive";
  const isManager = ["network_admin", "sales_manager_products", "accountant"].includes(user.role);
  const stageConfig = useConfig("b2b-stages");
  const STAGES = isExec
    ? EXEC_STAGES
    : stageConfig.length ? stageConfig.map((s) => s.name) : DEFAULT_STAGES;

  const [rows, setRows] = useState([]);
  const [studios, setStudios] = useState([]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [log, setLog] = useState({ kind: "Call", note: "" });
  const [notePrompt, setNotePrompt] = useState(null);
  const [fuForm, setFuForm] = useState({ next_followup: "", next_followup_note: "" });
  const [access, setAccess] = useState({ studio_id: "", note: "" });
  const [newStudioLogin, setNewStudioLogin] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [view, setView] = useState("board");
  const [plan, setPlan] = useState([]);
  const [calls, setCalls] = useState(null);
  const [openCity, setOpenCity] = useState(null);
  const canOrder = ["sales_manager_products", "accountant", "network_admin"].includes(user.role);
  const canAssign = ["sales_manager_products", "network_admin"].includes(user.role);
  const [execs, setExecs] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [orderProspect, setOrderProspect] = useState(null);
  const [orderLines, setOrderLines] = useState([{ sku_id: "", quantity: "" }]);
  const [orderHistory, setOrderHistory] = useState([]);

  const submitProspectOrder = async () => {
    const items = orderLines
      .filter((l) => l.sku_id && Number(l.quantity) > 0)
      .map((l) => ({ sku_id: l.sku_id, quantity: Number(l.quantity) }));
    if (!items.length) return toast.error("Add at least one SKU and quantity");
    try {
      await api.post(`/b2b/prospects/${orderProspect.id}/order`, { items });
      toast.success(`Product sale raised for ${orderProspect.business_name}`);
      const pid = orderProspect.id;
      setOrderProspect(null);
      setOrderLines([{ sku_id: "", quantity: "" }]);
      if (detail?.id === pid) api.get(`/b2b/prospects/${pid}/orders`).then(({ data }) => setOrderHistory(data));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const load = useCallback(() => {
    api
      .get("/b2b/prospects")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/b2b/visit-plan").then(({ data }) => setPlan(data)).catch(() => {});
    api.get("/b2b/accredited-studios").then(({ data }) => setCalls(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
    if (canOrder) api.get("/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    if (canAssign) api.get("/central-sales/executives").then(({ data }) => setExecs(data)).catch(() => {});
  }, [load]);

  const assignExec = async (execId) => {
    if (!detail) return;
    try {
      await api.patch(`/b2b/prospects/${detail.id}/assign`, { exec_id: execId === "none" ? "" : execId });
      toast.success("Lead owner updated");
      openDetail(detail.id);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not assign the lead");
    }
  };

  const saveFollowup = async () => {
    if (!detail) return;
    try {
      await api.patch(`/b2b/prospects/${detail.id}/followup`, {
        next_followup: fuForm.next_followup,
        next_followup_note: fuForm.next_followup_note,
      });
      toast.success(fuForm.next_followup ? "Follow-up reminder set" : "Follow-up cleared");
      openDetail(detail.id);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save the reminder");
    }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/b2b/prospects/${id}`);
    setDetail(data);
    setFuForm({
      next_followup: (data.next_followup || "").slice(0, 16),
      next_followup_note: data.next_followup_note || "",
    });
    setAccess({ studio_id: data.studio_id || "", note: data.access_note || "" });
    setNewStudioLogin(null);
    setLog({ kind: "Call", note: "" });
    if (canOrder) api.get(`/b2b/prospects/${id}/orders`).then(({ data: o }) => setOrderHistory(o)).catch(() => setOrderHistory([]));
    else setOrderHistory([]);
  };

  const quickLogB2B = async (prospectId, kind) => {
    if (!canWrite) return;
    try {
      const { data } = await api.post(`/b2b/prospects/${prospectId}/interactions`,
        { kind, note: "", outcome: "" });
      toast.success(`${kind} logged`);
      setNotePrompt({ prospectId, interactionId: data.id, kind, note: "", outcome: "", nextFollowup: "" });
      pingNotifications();
      if (detail?.id === prospectId) openDetail(prospectId);
      else load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveNotePrompt = async () => {
    if (!notePrompt) return;
    try {
      await api.patch(
        `/b2b/prospects/${notePrompt.prospectId}/interactions/${notePrompt.interactionId}`,
        { outcome: notePrompt.outcome, note: notePrompt.note });
      if (notePrompt.nextFollowup)
        await api.patch(`/b2b/prospects/${notePrompt.prospectId}/followup`,
          { next_followup: notePrompt.nextFollowup });
      toast.success(notePrompt.nextFollowup ? "Notes saved · follow-up scheduled" : "Notes saved");
      if (detail?.id === notePrompt.prospectId) openDetail(notePrompt.prospectId);
      else load();
      setNotePrompt(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const save = async () => {
    const payload = {
      ...form,
      expected_value: Number(form.expected_value || 0),
      proforma_value: Number(form.proforma_value || 0),
      payment_received: Number(form.payment_received || 0),
    };
    try {
      if (form.id) await api.put(`/b2b/prospects/${form.id}`, payload);
      else await api.post("/b2b/prospects", payload);
      toast.success(form.id ? "Prospect updated" : "Prospect added to the pipeline");
      setForm(null);
      load();
      if (form.id && detail?.id === form.id) openDetail(form.id);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const move = async (row, stage) => {
    try {
      await api.patch(`/b2b/prospects/${row.id}/stage`, null, { params: { stage } });
      toast.success(`${row.business_name} → ${stage}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addLog = async () => {
    try {
      await api.post(`/b2b/prospects/${detail.id}/interactions`, log);
      setLog({ kind: "Call", note: "" });
      pingNotifications();
      openDetail(detail.id);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const markAccess = async () => {
    try {
      await api.post(`/b2b/prospects/${detail.id}/access`, {
        studio_id: access.studio_id || null,
        note: access.note,
      });
      toast.success("Login marked as created");
      openDetail(detail.id);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const createStudioFromDetailer = async () => {
    try {
      const { data } = await api.post(`/b2b/prospects/${detail.id}/create-studio`, {});
      setNewStudioLogin(data);
      setDetail((d) => ({ ...d, ...data.prospect }));
      toast.success(`Studio "${data.studio.name}" created and linked`);
      api.get("/studios").then(({ data: s }) => setStudios(s)).catch(() => {});
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/b2b/prospects/${id}`);
      toast.success("Prospect removed");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const drop = (stage) => {
    setOverCol(null);
    if (!dragId) return;
    const row = rows.find((r) => r.id === dragId);
    setDragId(null);
    if (row && row.stage !== stage) move(row, stage);
  };

  const toggleCall = async (sid, called) => {
    try {
      await api.post("/b2b/studio-call", { studio_id: sid, called });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const setPrimary = async (sid, user_id) => {
    try {
      await api.put("/b2b/studio-primary-contact", { studio_id: sid, user_id });
      toast.success("Primary contact updated");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const empty = rows.length === 0;
  const pipelineValue = rows.reduce((t, r) => t + Number(r.expected_value || 0), 0);
  const pendingAccess = rows.filter((r) => r.access_status === "pending");
  const today = new Date().toISOString().slice(0, 10);
  const fuDate = (r) => (r.next_followup || "").slice(0, 10);
  const dueToday = rows.filter((r) => fuDate(r) === today);
  const overdue = rows.filter((r) => r.next_followup && fuDate(r) < today);
  const upcoming = rows
    .filter((r) => r.next_followup)
    .sort((a, b) => (a.next_followup < b.next_followup ? -1 : 1));
  const fmtFollowup = (v) => {
    if (!v) return "";
    if (v.includes("T")) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime()))
        return d.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    }
    return v;
  };
  const stageCounts = STAGES.map((s) => ({ stage: s, count: rows.filter((r) => r.stage === s).length }));
  const filteredRows = rows.filter(
    (r) =>
      (stageFilter === "all" || r.stage === stageFilter) &&
      (!search.trim() ||
        [r.business_name, r.owner_name, r.mobile, r.whatsapp, r.city]
          .some((x) => (x || "").toLowerCase().includes(search.trim().toLowerCase())))
  );

  return (
    <div>
      <PageHead
        title="B2B Detailer Pipeline"
        subtitle="Signing up new detailers — from first contact to an accredited studio buying every month"
      >
        {canWrite && (
          <Button data-testid="add-prospect-btn" onClick={() => setForm({ ...blankForm })}>
            <Plus className="mr-2 h-4 w-4" /> Add detailer
          </Button>
        )}
      </PageHead>

      {["network_admin", "sales_manager_products"].includes(user.role) && (
        <ExecutivesManager />
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Stat label={isExec ? "My detailers" : "Detailers in pipeline"} value={rows.length} testid="b2b-count" />
        {isExec ? (
          <Stat
            label="Follow-ups due"
            value={dueToday.length + overdue.length}
            tone={dueToday.length + overdue.length ? "text-crimson-600" : "text-brand-ink"}
            testid="b2b-exec-due-count"
          />
        ) : (
          <Stat label="Expected order value" value={money(pipelineValue)} testid="b2b-value" />
        )}
        {isExec ? (
          <Stat
            label="First purchases"
            value={rows.filter((r) => r.stage === "First purchase").length}
            tone="text-emerald-600"
            testid="b2b-exec-firstpurchase-count"
          />
        ) : (
          <Stat
            label="Logins pending"
            value={pendingAccess.length}
            tone={pendingAccess.length ? "text-amber-600" : "text-brand-ink"}
            testid="b2b-pending-access"
          />
        )}
      </div>

      {isExec && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="b2b-exec-stage-summary">
          {stageCounts.map((s) => (
            <div
              key={s.stage}
              data-testid={`b2b-exec-stage-count-${s.stage}`}
              className="rounded-lg border border-brand-line bg-white px-4 py-2"
            >
              <p className="text-xs font-medium text-slate-500">{s.stage}</p>
              <p className="font-display text-xl font-semibold text-brand-ink">{s.count}</p>
            </div>
          ))}
        </div>
      )}

      {isExec && (
        <div
          className="mb-6 rounded-md border border-crimson-200 bg-crimson-50/60 p-4"
          data-testid="b2b-exec-followups-due"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-crimson-800">
            <BellRing className="h-4 w-4" /> Follow-ups due today
          </p>
          {overdue.length === 0 && dueToday.length === 0 ? (
            <p className="mt-1.5 text-sm text-slate-600" data-testid="b2b-exec-followups-empty">
              You're all caught up — no follow-ups due today.
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {[...overdue, ...dueToday].map((r) => (
                <button
                  key={r.id}
                  data-testid={`b2b-exec-followup-chip-${r.id}`}
                  onClick={() => openDetail(r.id)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${
                    r.next_followup < today
                      ? "bg-crimson-600 text-white hover:bg-crimson-700"
                      : "bg-white text-crimson-700 hover:bg-crimson-100"
                  }`}
                >
                  {r.business_name} · {r.next_followup}
                  {r.next_followup < today ? " (overdue)" : ""}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {pendingAccess.length > 0 && (
        <div
          className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          data-testid="b2b-pending-banner"
        >
          <p className="font-semibold">
            {pendingAccess.length} accredited detailer(s) still waiting for a login
          </p>
          <p className="mt-1 text-xs">
            {pendingAccess.map((p) => p.business_name).join(" · ")} — open the card and mark the
            login as created once their user id is set up.
          </p>
        </div>
      )}

      {!isExec && (dueToday.length > 0 || overdue.length > 0) && (
        <div
          className="mb-6 rounded-md border border-crimson-200 bg-crimson-50/60 p-4"
          data-testid="b2b-followup-strip"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-crimson-800">
            <BellRing className="h-4 w-4" />
            {overdue.length > 0 && `${overdue.length} follow-up(s) overdue`}
            {overdue.length > 0 && dueToday.length > 0 && " · "}
            {dueToday.length > 0 && `${dueToday.length} due today`}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {[...overdue, ...dueToday].map((r) => (
              <button
                key={r.id}
                data-testid={`b2b-followup-chip-${r.id}`}
                onClick={() => openDetail(r.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${
                  r.next_followup < today
                    ? "bg-crimson-600 text-white hover:bg-crimson-700"
                    : "bg-white text-crimson-700 hover:bg-crimson-100"
                }`}
              >
                {r.business_name} · {fmtFollowup(r.next_followup)}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-crimson-700">
            Logging an interaction or moving the follow-up date clears the nudge.
          </p>
        </div>
      )}

      {!isExec && upcoming.length > 0 && (
        <div
          className="mb-6 rounded-md border border-brand-line bg-white p-4"
          data-testid="b2b-upcoming-followups"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
            <BellRing className="h-4 w-4 text-brand" /> Upcoming follow-ups
          </p>
          <div className="mt-2 divide-y divide-slate-100">
            {upcoming.slice(0, 8).map((r) => (
              <button
                key={r.id}
                data-testid={`b2b-upcoming-${r.id}`}
                onClick={() => openDetail(r.id)}
                className="flex w-full items-start justify-between gap-3 py-2 text-left hover:bg-brand-tint/40"
              >
                <div>
                  <p className="text-sm font-medium text-brand-ink">{r.business_name}</p>
                  {r.next_followup_note && (
                    <p className="text-xs text-slate-500">{r.next_followup_note}</p>
                  )}
                </div>
                <span className={`shrink-0 text-xs font-medium ${
                  fuDate(r) < today ? "text-crimson-700" : "text-slate-500"
                }`}>
                  {fmtFollowup(r.next_followup)}{fuDate(r) < today ? " · overdue" : ""}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!isExec && (
      <div className="mb-6 flex flex-wrap gap-2">
        {[["board", "Pipeline board"], ["plan", "Visit planner"], ["calls", "Daily calls"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`b2b-view-${k}`}
            onClick={() => setView(k)}
            className={`min-h-[44px] rounded-full px-4 text-sm font-medium transition-colors duration-200 ${
              view === k ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-brand-tint"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      )}

      {!isExec && view === "calls" && (
        <div data-testid="b2b-daily-calls">
          <div className="mb-4 rounded-md border border-brand-line bg-white p-4">
            <p className="text-sm text-slate-600">
              Call every accredited studio once a day. Tap the owner's number to call, then mark it
              done. This list is always the full active accredited network.
            </p>
            <p className="mt-2 font-display text-2xl font-semibold text-brand-ink" data-testid="calls-progress">
              {calls ? `${calls.called} / ${calls.total}` : "…"}{" "}
              <span className="text-sm font-normal text-slate-500">studios called today</span>
            </p>
          </div>
          {!calls || calls.studios.length === 0 ? (
            <p className="rounded-md border border-brand-line bg-white p-6 text-sm text-slate-500">
              No accredited studios found.
            </p>
          ) : (
            <div className="space-y-2">
              {calls.studios.map((s) => (
                <Card
                  key={s.studio_id}
                  className={`border shadow-none ${s.called_today ? "border-emerald-200 bg-emerald-50/50" : "border-brand-line"}`}
                  data-testid={`call-row-${s.studio_id}`}
                >
                  <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-[180px]">
                      <p className="font-display text-base font-semibold text-brand-ink">{s.name}</p>
                      <p className="text-xs text-slate-500">
                        {s.primary
                          ? `${s.primary.name || "Owner"} · ${s.primary.mobile || "no number"}`
                          : "No owner contact on file"}
                        {s.city ? ` · ${s.city}` : ""}
                      </p>
                      {s.called_today && s.called_at && (
                        <p className="mt-1 text-xs font-medium text-emerald-700" data-testid={`call-time-${s.studio_id}`}>
                          Called {fmtDateTime(s.called_at)}
                          {s.called_by ? ` · ${s.called_by}` : ""}
                        </p>
                      )}
                      {s.contacts.length > 1 && canWrite && (
                        <div className="mt-2 flex items-center gap-2">
                          <Label className="text-[11px] text-slate-500">Primary contact</Label>
                          <Select
                            value={s.primary?.user_id || ""}
                            onValueChange={(v) => setPrimary(s.studio_id, v)}
                          >
                            <SelectTrigger className="h-8 w-48 text-xs" data-testid={`call-primary-select-${s.studio_id}`}>
                              <SelectValue placeholder="Choose contact" />
                            </SelectTrigger>
                            <SelectContent>
                              {s.contacts.map((c) => (
                                <SelectItem key={c.user_id} value={c.user_id}>
                                  {c.name} ({c.role === "studio_owner" ? "Owner" : "Manager"})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {s.primary?.mobile && (
                        <a href={telLink(s.primary.mobile)}>
                          <Button size="sm" variant="outline" data-testid={`call-dial-${s.studio_id}`}>
                            <Phone className="mr-1.5 h-3.5 w-3.5" /> Call
                          </Button>
                        </a>
                      )}
                      {canWrite && (
                        <Button
                          size="sm"
                          variant={s.called_today ? "outline" : "default"}
                          data-testid={`call-toggle-${s.studio_id}`}
                          onClick={() => toggleCall(s.studio_id, !s.called_today)}
                        >
                          {s.called_today ? (
                            <>
                              <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Called
                              {s.called_by ? ` · ${s.called_by}` : ""}
                            </>
                          ) : (
                            "Mark called"
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {!isExec && view === "plan" && (
        <div className="space-y-2" data-testid="b2b-visit-plan">
          {plan.length === 0 && (
            <p className="rounded-md border border-brand-line bg-white p-6 text-sm text-slate-500">
              Add detailers with a city and they will group here so one trip covers several visits.
            </p>
          )}
          {plan.map((c) => (
            <Card key={c.city} className="border-brand-line shadow-none" data-testid={`b2b-city-${c.city}`}>
              <button
                className="w-full p-5 text-left"
                data-testid={`b2b-city-toggle-${c.city}`}
                onClick={() => setOpenCity((x) => (x === c.city ? null : c.city))}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 font-display text-base font-semibold text-brand-ink">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {c.city}
                      {c.region ? (
                        <span className="text-sm font-normal text-slate-500">· {c.region}</span>
                      ) : null}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {c.prospects} detailer(s) · {money(c.expected_value)} expected
                      {c.accredited ? ` · ${c.accredited} accredited` : ""}
                    </p>
                  </div>
                  {c.due > 0 && (
                    <Badge className="border-0 bg-crimson-100 text-crimson-700">
                      {c.due} follow-up(s) due
                    </Badge>
                  )}
                </div>
              </button>
              {openCity === c.city && (
                <CardContent className="border-t border-slate-100 pt-4">
                  <div className="space-y-1.5">
                    {c.detailers.map((d) => (
                      <button
                        key={d.id}
                        data-testid={`b2b-city-detailer-${d.id}`}
                        onClick={() => openDetail(d.id)}
                        className="flex w-full flex-wrap items-center justify-between gap-2 rounded-md border border-brand-line px-3 py-2 text-left text-sm transition-colors duration-200 hover:bg-brand-tint/60"
                      >
                        <span>
                          <span className="font-medium text-brand-ink">{d.business_name}</span>
                          <span className="block text-xs text-slate-500">
                            {[d.owner_name, d.mobile].filter(Boolean).join(" · ") || "No contact yet"}
                          </span>
                        </span>
                        <span className="text-right text-xs">
                          <span className="block text-slate-600">{d.stage}</span>
                          <span className={d.overdue ? "text-crimson-700" : "text-slate-500"}>
                            {d.next_followup ? `follow up ${d.next_followup}` : "no follow-up set"}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {!isExec && view === "board" && empty && (
        <div className="mb-4 rounded-md border border-brand-line bg-white p-5" data-testid="b2b-empty-state">
          <p className="font-display text-base font-semibold text-brand-ink">
            This is the B2B pipeline — it fills up left to right
          </p>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Every detailer you approach moves through these stages: a Lead becomes Interested, you
            book and make a Visit, send a Proforma, take Payment, Dispatch and Deliver the material,
            watch their First work, then Next purchases until they become an Accredited detailer
            with their own login and, finally, Monthly purchases.
          </p>
          {canWrite && (
            <Button className="mt-4" data-testid="b2b-empty-add-btn" onClick={() => setForm({ ...blankForm })}>
              <Plus className="mr-2 h-4 w-4" /> Add your first detailer
            </Button>
          )}
        </div>
      )}

      {!isExec && view === "board" && (
      <div className="flex flex-col gap-4 pb-4 lg:flex-row lg:overflow-x-auto" data-testid="b2b-board">
        {STAGES.map((stage, i) => {
          const cards = rows.filter((r) => r.stage === stage);
          return (
            <div
              key={stage}
              data-testid={`b2b-column-${stage}`}
              onDragOver={(e) => {
                if (!canWrite) return;
                e.preventDefault();
                setOverCol(stage);
              }}
              onDragLeave={() => setOverCol((c) => (c === stage ? null : c))}
              onDrop={() => canWrite && drop(stage)}
              className={`w-full shrink-0 rounded-md border p-3 transition-colors duration-200 lg:w-[264px] ${
                overCol === stage ? "border-crimson-400 bg-crimson-50/60" : "border-brand-line bg-white"
              }`}
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                  {empty ? `${i + 1}. ${stage}` : stage}
                </span>
                <span
                  data-testid={`b2b-count-${stage}`}
                  className="text-xs font-semibold text-slate-500"
                >
                  {cards.length}
                </span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
                {cards.length === 0 &&
                  (empty ? (
                    <p
                      data-testid={`b2b-stage-hint-${stage}`}
                      className="w-full shrink-0 rounded-md border border-dashed border-brand-line bg-slate-50/60 p-3 text-xs leading-relaxed text-slate-500"
                    >
                      {STAGE_HINT[stage] || "Detailers at this stage show up here."}
                    </p>
                  ) : (
                    <p className="w-full shrink-0 py-6 text-center text-xs text-slate-400">Drop detailers here</p>
                  ))}
                {cards.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`b2b-card-${r.id}`}
                    draggable={canWrite}
                    onDragStart={() => setDragId(r.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => openDetail(r.id)}
                    className={`w-60 shrink-0 cursor-pointer rounded-md border border-brand-line bg-white p-3 transition-shadow duration-200 hover:shadow-sm lg:w-full ${
                      dragId === r.id ? "opacity-50" : ""
                    }`}
                  >
                    <p className="truncate text-sm font-semibold text-brand-ink">
                      {r.business_name}
                    </p>
                    <div
                      className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        r.assigned_exec_name ? "bg-brand-tint text-brand" : "bg-slate-100 text-slate-500"
                      }`}
                      data-testid={`b2b-exec-badge-${r.id}`}
                    >
                      <UserRound className="h-3 w-3" strokeWidth={2} />
                      {r.assigned_exec_name || "Unassigned"}
                    </div>
                    {r.owner_name && (
                      <p className="mt-0.5 truncate text-xs text-slate-500">{r.owner_name}</p>
                    )}
                    {r.mobile && (
                      <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-slate-500">
                        <Phone className="h-3 w-3" strokeWidth={1.8} />
                        {r.mobile}
                        {r.city ? ` · ${r.city}` : ""}
                      </p>
                    )}
                    <p className="mt-2 flex items-center gap-1 text-xs font-semibold text-slate-700">
                      <IndianRupee className="h-3 w-3" strokeWidth={2} />
                      {money(r.expected_value || 0).replace("₹", "")}
                    </p>
                    {r.balance_due > 0 && (
                      <p className="mt-0.5 text-xs text-rose-600">
                        {money(r.balance_due)} pending
                      </p>
                    )}
                    {r.access_status === "pending" && (
                      <Badge className="mt-2 border-0 bg-amber-100 text-amber-800">
                        Login pending
                      </Badge>
                    )}
                    {r.access_status === "done" && (
                      <Badge className="mt-2 border-0 bg-emerald-50 text-emerald-700">
                        <ShieldCheck className="mr-1 h-3 w-3" /> Login created
                      </Badge>
                    )}
                    {r.is_accredited_studio && (
                      <Badge
                        className="mt-2 ml-1 border-0 bg-brand-tint text-brand"
                        data-testid={`b2b-network-studio-${r.id}`}
                      >
                        Network studio
                      </Badge>
                    )}
                    {r.recent_interactions?.length > 0 && (
                      <div
                        className="mt-2 space-y-1 border-t border-brand-line pt-2"
                        data-testid={`b2b-timeline-${r.id}`}
                      >
                        {r.recent_interactions.map((it, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-1.5 text-[11px] leading-tight text-slate-500"
                          >
                            <span
                              className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
                                KIND_DOT[it.kind] || "bg-slate-300"
                              }`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="font-medium text-slate-600">{it.kind}</span>
                              {it.happened_on ? ` · ${it.happened_on}` : ""}
                              {it.outcome ? (
                                <span className="block truncate text-slate-400">{it.outcome}</span>
                              ) : null}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {r.mobile && (
                      <div className="mt-2 flex gap-2 border-t border-brand-line pt-2">
                        <button
                          type="button"
                          data-testid={`b2b-card-call-${r.id}`}
                          onClick={(e) => { e.stopPropagation(); window.location.href = telLink(r.mobile); quickLogB2B(r.id, "Call"); }}
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-brand-line py-1.5 text-xs font-medium text-brand-ink hover:bg-brand-tint"
                        >
                          <Phone className="h-3.5 w-3.5" /> Call
                        </button>
                        <button
                          type="button"
                          data-testid={`b2b-card-whatsapp-${r.id}`}
                          onClick={(e) => { e.stopPropagation(); window.open(waLink(r.mobile), "_blank"); quickLogB2B(r.id, "WhatsApp"); }}
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-emerald-200 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                        >
                          <MessageSquare className="h-3.5 w-3.5" /> WhatsApp
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {isExec && (
        <div data-testid="b2b-exec-list">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Input
              className="min-h-[44px] w-full sm:w-72"
              placeholder="Search name, studio, phone or city"
              data-testid="b2b-exec-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="min-h-[44px] w-48" data-testid="b2b-exec-stage-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {EXEC_STAGES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-slate-500" data-testid="b2b-exec-result-count">
              {filteredRows.length} detailer(s)
            </span>
          </div>

          {rows.length === 0 ? (
            <div className="rounded-md border border-brand-line bg-white p-6" data-testid="b2b-exec-empty">
              <p className="font-display text-base font-semibold text-brand-ink">
                No detailers yet
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Add your first detailer prospect to start working your pipeline.
              </p>
              <Button className="mt-4" data-testid="b2b-exec-empty-add" onClick={() => setForm({ ...blankForm })}>
                <Plus className="mr-2 h-4 w-4" /> Add detailer
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRows.length === 0 && (
                <p className="rounded-md border border-brand-line bg-white p-6 text-sm text-slate-500" data-testid="b2b-exec-no-match">
                  No detailers match your search / filter.
                </p>
              )}
              {filteredRows.map((r) => (
                <Card
                  key={r.id}
                  className="border-brand-line shadow-none"
                  data-testid={`b2b-exec-row-${r.id}`}
                >
                  <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <button
                      className="min-w-[200px] flex-1 text-left"
                      data-testid={`b2b-exec-open-${r.id}`}
                      onClick={() => openDetail(r.id)}
                    >
                      <p className="font-display text-base font-semibold text-brand-ink">
                        {r.business_name}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {[r.owner_name, r.mobile, r.city].filter(Boolean).join(" · ") || "No contact yet"}
                      </p>
                      {r.next_followup && (
                        <p
                          className={`mt-1 text-xs font-medium ${
                            r.next_followup < today ? "text-crimson-700" : "text-slate-500"
                          }`}
                          data-testid={`b2b-exec-followup-${r.id}`}
                        >
                          Follow-up {r.next_followup}{r.next_followup < today ? " (overdue)" : ""}
                        </p>
                      )}
                    </button>
                    <div className="flex flex-wrap items-center gap-2">
                      {(r.mobile || r.whatsapp) && (
                        <>
                          <a href={telLink(r.whatsapp || r.mobile)}>
                            <Button size="sm" variant="outline" data-testid={`b2b-exec-call-${r.id}`}>
                              <Phone className="mr-1.5 h-3.5 w-3.5" /> Call
                            </Button>
                          </a>
                          <a href={waLink(r.whatsapp || r.mobile)} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`b2b-exec-whatsapp-${r.id}`}>
                              <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                            </Button>
                          </a>
                        </>
                      )}
                      <Select value={r.stage} onValueChange={(v) => move(r, v)}>
                        <SelectTrigger className="h-9 w-40 text-xs" data-testid={`b2b-exec-stage-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXEC_STAGES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------ add / edit */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Edit detailer" : "Add detailer"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Business name *" testid="b2b-business-input"
                value={form.business_name}
                onChange={(v) => setForm((f) => ({ ...f, business_name: v }))} />
              <Field label="Owner name" testid="b2b-owner-input" value={form.owner_name}
                onChange={(v) => setForm((f) => ({ ...f, owner_name: v }))} />
              <Field label="Mobile" testid="b2b-mobile-input" value={form.mobile}
                onChange={(v) => setForm((f) => ({ ...f, mobile: v }))} />
              <Field label="WhatsApp number" testid="b2b-whatsapp-input" value={form.whatsapp}
                onChange={(v) => setForm((f) => ({ ...f, whatsapp: v }))} />
              {!isExec && (
                <Field label="Email" testid="b2b-email-input" value={form.email}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
              )}
              <Field label="City" testid="b2b-city-input" value={form.city}
                onChange={(v) => setForm((f) => ({ ...f, city: v }))} />
              <Field label="State" testid="b2b-state-input" value={form.state}
                onChange={(v) => setForm((f) => ({ ...f, state: v }))} />
              {!isExec && (
                <Field label="Region" testid="b2b-region-input" value={form.region}
                  onChange={(v) => setForm((f) => ({ ...f, region: v }))} />
              )}
              {!isExec && (
                <Field label="GSTIN (for proforma)" testid="b2b-gstin-input" value={form.gstin}
                  onChange={(v) => setForm((f) => ({ ...f, gstin: v }))} />
              )}
              {!isExec && (
                <Field label="Billing address (for proforma)" testid="b2b-address-input" value={form.address}
                  onChange={(v) => setForm((f) => ({ ...f, address: v }))} />
              )}
              <div>
                <Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm((f) => ({ ...f, stage: v }))}>
                  <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="b2b-stage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Next follow-up (date & time)" testid="b2b-followup-input" type="datetime-local"
                value={form.next_followup}
                onChange={(v) => setForm((f) => ({ ...f, next_followup: v }))} />
              <Field label="Follow-up note (what it's for)" testid="b2b-followup-note-input"
                value={form.next_followup_note}
                onChange={(v) => setForm((f) => ({ ...f, next_followup_note: v }))} />
              {!isExec && (
                <Field label="Expected order value (₹)" testid="b2b-expected-input"
                  value={form.expected_value}
                  onChange={(v) => setForm((f) => ({ ...f, expected_value: v }))} />
              )}
              {!isExec && (
                <Field label="Proforma value (₹)" testid="b2b-proforma-input"
                  value={form.proforma_value}
                  onChange={(v) => setForm((f) => ({ ...f, proforma_value: v }))} />
              )}
              {!isExec && (
                <Field label="Payment received (₹)" testid="b2b-payment-input"
                  value={form.payment_received}
                  onChange={(v) => setForm((f) => ({ ...f, payment_received: v }))} />
              )}
              <div className="sm:col-span-2">
                <Label>Notes</Label>
                <Textarea className="mt-1.5" rows={3} data-testid="b2b-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-prospect-btn" onClick={save}
              disabled={!form?.business_name?.trim()}>
              {form?.id ? "Save changes" : "Add to pipeline"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------ detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-400" />
              {detail?.business_name}
              <Badge className="border-0 bg-brand-tint text-brand">{detail?.stage}</Badge>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6" data-testid="b2b-detail">
              <div className="grid gap-3 text-sm sm:grid-cols-3">
                <Info label="Owner" value={detail.owner_name || "—"} />
                <Info label="Mobile" value={detail.mobile || "—"} />
                <Info label="WhatsApp" value={detail.whatsapp || "—"} />
                <Info label="City" value={[detail.city, detail.region].filter(Boolean).join(" · ") || "—"} />
                {!isExec && <Info label="Expected value" value={money(detail.expected_value || 0)} />}
                {!isExec && <Info label="Proforma" value={money(detail.proforma_value || 0)} />}
                {!isExec && (
                  <Info
                    label="Received / balance"
                    value={`${money(detail.payment_received || 0)} · ${money(detail.balance_due || 0)} due`}
                  />
                )}
                <Info label="Next follow-up" value={fmtFollowup(detail.next_followup) || "—"} />
              </div>
              {canAssign && (
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-brand-line bg-white p-3"
                     data-testid="b2b-assign-row">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
                    <UserRound className="h-4 w-4 text-brand" /> Lead owner (executive)
                  </div>
                  <Select value={detail.assigned_exec_id || "none"} onValueChange={assignExec}>
                    <SelectTrigger className="w-56" data-testid="b2b-assign-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {execs.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {canWrite && (
                <Card className="border-brand-line bg-brand-tint/30 shadow-none" data-testid="b2b-followup-reminder">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BellRing className="h-4 w-4 text-brand" /> Follow-up reminder
                    </CardTitle>
                    {detail.next_followup_note && (
                      <p className="mt-1 text-xs text-slate-600">Current note: {detail.next_followup_note}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs">When (date &amp; time)</Label>
                        <Input
                          type="datetime-local"
                          className="mt-1"
                          data-testid="b2b-detail-followup-when"
                          value={fuForm.next_followup}
                          onChange={(e) => setFuForm((f) => ({ ...f, next_followup: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">What it's for</Label>
                        <Input
                          className="mt-1"
                          data-testid="b2b-detail-followup-note"
                          placeholder="e.g. Call about proforma"
                          value={fuForm.next_followup_note}
                          onChange={(e) => setFuForm((f) => ({ ...f, next_followup_note: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      {detail.next_followup && (
                        <Button variant="ghost" size="sm" data-testid="b2b-detail-followup-clear"
                          onClick={() => { setFuForm({ next_followup: "", next_followup_note: "" }); }}>
                          Clear
                        </Button>
                      )}
                      <Button size="sm" data-testid="b2b-detail-followup-save" onClick={saveFollowup}>
                        Set reminder
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {(detail.mobile || detail.whatsapp) && (
                <div className="flex flex-wrap gap-2" data-testid="b2b-contact-actions">
                  <Button
                    variant="outline"
                    className="min-h-[44px]"
                    data-testid="b2b-call-btn"
                    onClick={() => {
                      window.location.href = telLink(detail.whatsapp || detail.mobile);
                      quickLogB2B(detail.id, "Call");
                    }}
                  >
                    <Phone className="mr-2 h-4 w-4" /> Call
                  </Button>
                  <Button
                    variant="outline"
                    className="min-h-[44px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    data-testid="b2b-whatsapp-btn"
                    onClick={() => {
                      window.open(waLink(detail.whatsapp || detail.mobile), "_blank");
                      quickLogB2B(detail.id, "WhatsApp");
                    }}
                  >
                    <MessageSquare className="mr-2 h-4 w-4" /> WhatsApp
                  </Button>
                  {isManager && <ProformaDialog prospect={detail} />}
                </div>
              )}
              {isManager && !detail.mobile && !detail.whatsapp && (
                <div data-testid="b2b-proforma-row"><ProformaDialog prospect={detail} /></div>
              )}
              {detail.notes && <p className="text-sm text-slate-600">{detail.notes}</p>}

              {detail.access_status !== "not_required" && (
                <Card
                  className={`border shadow-none ${
                    detail.access_status === "pending" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50/50"
                  }`}
                  data-testid="b2b-access-card"
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      {detail.access_status === "pending"
                        ? "Platform login pending"
                        : "Platform login created"}
                    </CardTitle>
                    <p className="mt-1 text-xs text-slate-600">
                      {detail.access_status === "pending"
                        ? "This detailer is accredited. Create their studio + login below (or link an existing studio), then it's connected both ways."
                        : "Login created."}
                    </p>
                    {detail.access_status === "done" && (
                      <p className="mt-1 text-sm" data-testid="b2b-linked-studio">
                        Linked studio:{" "}
                        {detail.studio_id ? (
                          <Link to={`/studios/${detail.studio_id}`} className="font-semibold text-brand-ink underline">
                            {studios.find((s) => s.id === detail.studio_id)?.name || "View studio"}
                          </Link>
                        ) : (
                          <span className="text-slate-500">not linked</span>
                        )}
                        {detail.access_note ? ` · ${detail.access_note}` : ""}
                      </p>
                    )}
                  </CardHeader>
                  {isManager && detail.access_status === "pending" && (
                    <CardContent className="space-y-3">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="min-w-[14rem]">
                          <Label className="text-xs">Link an existing studio</Label>
                          <Select
                            value={access.studio_id}
                            onValueChange={(v) => setAccess((a) => ({ ...a, studio_id: v }))}
                          >
                            <SelectTrigger className="mt-1 min-h-[44px]" data-testid="b2b-access-studio-select">
                              <SelectValue placeholder="Select studio" />
                            </SelectTrigger>
                            <SelectContent>
                              {studios.map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Input
                          className="min-h-[44px] flex-1"
                          placeholder="User id / note (optional)"
                          data-testid="b2b-access-note-input"
                          value={access.note}
                          onChange={(e) => setAccess((a) => ({ ...a, note: e.target.value }))}
                        />
                        <Button data-testid="b2b-mark-access-btn" onClick={markAccess}>
                          <ShieldCheck className="mr-2 h-4 w-4" /> Link studio
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">or</span>
                        <Button
                          variant="outline"
                          className="border-brand-ink/20"
                          data-testid="b2b-create-studio-btn"
                          onClick={createStudioFromDetailer}
                        >
                          <Building2 className="mr-2 h-4 w-4" /> Create studio + login from this detailer
                        </Button>
                      </div>
                    </CardContent>
                  )}
                  {newStudioLogin && (
                    <CardContent>
                      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="b2b-new-studio-login">
                        <p className="font-semibold text-emerald-800">
                          Studio "{newStudioLogin.studio.name}" created and linked.
                        </p>
                        {newStudioLogin.owner ? (
                          <>
                            <p className="mt-1">Owner login — Mobile: <b>{newStudioLogin.owner.mobile}</b>, Password: <b>{newStudioLogin.default_login_code}</b></p>
                            <Button
                              variant="outline"
                              className="mt-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                              data-testid="b2b-new-studio-whatsapp-btn"
                              onClick={() => {
                                const msg =
                                  `Welcome to the team. Your login details for Veloskin PPF Network: ` +
                                  `Mobile: ${newStudioLogin.owner.mobile}, Password: ${newStudioLogin.default_login_code}. ` +
                                  `Please log in and change your password after first login.`;
                                window.open(`${waLink(newStudioLogin.owner.mobile)}?text=${encodeURIComponent(msg)}`, "_blank");
                              }}
                            >
                              <MessageSquare className="mr-2 h-4 w-4" /> Send Login Details via WhatsApp
                            </Button>
                          </>
                        ) : (
                          <p className="mt-1 text-xs text-slate-600">
                            Add the owner's login from Users (the detailer had no free mobile to auto-create one).
                          </p>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}

              {detail.monthly_purchases?.length > 0 && (
                <div data-testid="b2b-monthly-purchases">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Monthly purchases — real HQ order value
                  </p>
                  <div className="mt-2 space-y-1">
                    {detail.monthly_purchases.map((m) => (
                      <div
                        key={m.month}
                        data-testid={`b2b-month-${m.month}`}
                        className="flex items-center justify-between rounded-md border border-brand-line px-3 py-2 text-sm"
                      >
                        <span className="font-medium text-slate-800">{m.month}</span>
                        <span className="text-slate-600">
                          {m.orders} order(s) · {m.delivered} delivered ·{" "}
                          <strong className="text-brand-ink">{money(m.value)}</strong>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {canOrder && (
                <div data-testid="b2b-orders-section">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Product orders
                    </p>
                    <Button
                      size="sm"
                      data-testid="b2b-raise-order-btn"
                      onClick={() => { setOrderProspect(detail); setOrderLines([{ sku_id: "", quantity: "" }]); }}
                    >
                      <Plus className="mr-1.5 h-4 w-4" /> Raise product sale
                    </Button>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {orderHistory.length === 0 && (
                      <p className="text-sm text-slate-500">No product orders raised yet.</p>
                    )}
                    {orderHistory.map((o) => (
                      <div
                        key={o.id}
                        data-testid={`b2b-order-${o.id}`}
                        className="flex items-center justify-between rounded-md border border-brand-line px-3 py-2 text-sm"
                      >
                        <span className="text-slate-700">
                          {(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ") || "—"}
                        </span>
                        <span className="text-right">
                          <span className="block font-semibold text-brand-ink">{money(o.total_payable || 0)}</span>
                          <span className="text-xs text-slate-500">{o.status}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Interaction log
                </p>
                {canWrite && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <Select value={log.kind} onValueChange={(v) => setLog((l) => ({ ...l, kind: v }))}>
                      <SelectTrigger className="min-h-[44px] w-36" data-testid="b2b-log-kind-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {KINDS.map((k) => (
                          <SelectItem key={k} value={k}>{k}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className="min-h-[44px] flex-1"
                      placeholder="What happened?"
                      data-testid="b2b-log-note-input"
                      value={log.note}
                      onChange={(e) => setLog((l) => ({ ...l, note: e.target.value }))}
                    />
                    <Button data-testid="b2b-log-save-btn" disabled={!log.note.trim()} onClick={addLog}>
                      Log it
                    </Button>
                  </div>
                )}
                <div className="mt-3 space-y-1.5">
                  {(detail.interactions || []).length === 0 && (
                    <p className="text-sm text-slate-500">Nothing logged yet.</p>
                  )}
                  {(detail.interactions || []).map((x) => (
                    <div key={x.id} className="rounded-md border border-brand-line p-3 text-sm">
                      {x.note && <p className="text-slate-800">{x.note}</p>}
                      <p className="mt-0.5 text-xs text-slate-500">
                        {x.kind} · {fmtDateTime(x.created_at || x.happened_on)}
                        {x.by ? ` · ${x.by}` : ""}
                      </p>
                      {x.outcome ? (
                        <p className="mt-1 text-sm text-brand-ink">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Response: </span>
                          {x.outcome}
                        </p>
                      ) : (
                        canWrite && (
                          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2" data-testid={`b2b-pending-response-${x.id}`}>
                            <p className="mb-1 text-xs font-medium text-amber-700">Response pending:</p>
                            <OutcomeSelect
                              patchUrl={`/b2b/prospects/${detail.id}/interactions/${x.id}`}
                              testid={`b2b-outcome-${x.id}`}
                              compact
                              onSaved={() => openDetail(detail.id)}
                            />
                          </div>
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {canWrite && (
              <>
                <Button
                  variant="outline"
                  data-testid="b2b-edit-btn"
                  onClick={() => {
                    setForm({ ...detail });
                  }}
                >
                  Edit details
                </Button>
                {!detail?.is_accredited_studio && (
                  <Button
                    variant="ghost"
                    className="text-rose-600"
                    data-testid="b2b-delete-btn"
                    onClick={() => remove(detail.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Remove
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------ raise product sale */}
      <Dialog open={!!orderProspect} onOpenChange={(o) => !o && setOrderProspect(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="b2b-order-dialog">
          <DialogHeader>
            <DialogTitle>Raise product sale — {orderProspect?.business_name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Order links to this detailer and shows in their order history. Prices use the HQ sale price.
          </p>
          <div className="space-y-2">
            {orderLines.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <Select
                  value={l.sku_id}
                  onValueChange={(v) => setOrderLines((ls) => ls.map((x, idx) => (idx === i ? { ...x, sku_id: v } : x)))}
                >
                  <SelectTrigger className="min-h-[44px] flex-1" data-testid={`b2b-order-sku-${i}`}>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {catalog.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="w-24 min-h-[44px]"
                  inputMode="decimal"
                  placeholder="Qty"
                  data-testid={`b2b-order-qty-${i}`}
                  value={l.quantity}
                  onChange={(e) => setOrderLines((ls) => ls.map((x, idx) => (idx === i ? { ...x, quantity: e.target.value } : x)))}
                />
                {orderLines.length > 1 && (
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-500"
                    data-testid={`b2b-order-remove-${i}`}
                    onClick={() => setOrderLines((ls) => ls.filter((_, idx) => idx !== i))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" data-testid="b2b-order-add-line"
              onClick={() => setOrderLines((ls) => [...ls, { sku_id: "", quantity: "" }])}>
              <Plus className="mr-1.5 h-4 w-4" /> Add product
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderProspect(null)}>Cancel</Button>
            <Button data-testid="b2b-order-submit" onClick={submitProspectOrder}>Raise sale</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!notePrompt} onOpenChange={(o) => !o && setNotePrompt(null)}>
        <DialogContent className="sm:max-w-md" data-testid="b2b-note-prompt-dialog">
          <DialogHeader>
            <DialogTitle>Add {notePrompt?.kind} notes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Your {notePrompt?.kind?.toLowerCase()} has been logged. Add what was discussed and the
              outcome.
            </p>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                data-testid="b2b-note-prompt-note"
                className="mt-1"
                rows={3}
                placeholder="What was discussed?"
                value={notePrompt?.note || ""}
                onChange={(e) => setNotePrompt((n) => ({ ...n, note: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Outcome</Label>
              <Input
                data-testid="b2b-note-prompt-outcome"
                className="mt-1"
                placeholder="e.g. Interested, will send list / No answer / Order placed"
                value={notePrompt?.outcome || ""}
                onChange={(e) => setNotePrompt((n) => ({ ...n, outcome: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Next follow-up (optional)</Label>
              <Input
                type="date"
                data-testid="b2b-note-prompt-followup"
                className="mt-1"
                value={notePrompt?.nextFollowup || ""}
                onChange={(e) => setNotePrompt((n) => ({ ...n, nextFollowup: e.target.value }))}
              />
              <p className="mt-1 text-xs text-slate-400">
                Schedule the next nudge — the current one just cleared.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="b2b-note-prompt-skip" onClick={() => setNotePrompt(null)}>
              Skip
            </Button>
            <Button data-testid="b2b-note-prompt-save" onClick={saveNotePrompt}>
              Save notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, tone = "text-brand-ink", testid }) => (
  <Card className="border-brand-line shadow-none">
    <CardContent className="p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p data-testid={testid} className={`mt-1 font-display text-2xl font-semibold ${tone}`}>
        {value}
      </p>
    </CardContent>
  </Card>
);

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="font-medium text-brand-ink">{value}</p>
  </div>
);

const Field = ({ label, value, onChange, testid, type }) => (
  <div>
    <Label>{label}</Label>
    <Input
      className="mt-1.5 min-h-[44px]"
      type={type}
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);
